import * as THREE from "three";
import React, { useEffect, useRef, useState } from "react";
import { useGLTF, useAnimations, PerspectiveCamera, Environment } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";

const color = new THREE.Color();

const MESH_ITEMS = [
  {
    name: "Headphones",
    position: [-26.702, 27.649, -21.285],
    rotation: [-2.224, 1.248, 2.32],
    scale: 1.2,
  },
  {
    name: "Notebook",
    position: [0.772, 17.744, -42.137],
    rotation: [2.156, 1.214, -2.005],
    scale: 1.2,
  },
  {
    name: "Rocket003",
    position: [-4.293, -13.121, 24.847],
    rotation: [2.003, 1.127, -1.818],
    scale: 1.2,
  },
  {
    name: "Roundcube001",
    position: [22.581, 13.938, -27.753],
    rotation: [-2.522, 1.072, 2.061],
    scale: 1.3,
  },
  {
    name: "Table",
    position: [37.245, 5.919, -13.34],
    rotation: [1.891, 1.214, -2.005],
    scale: 1,
  },
  {
    name: "VR_Headset",
    position: [-15.903, 47.949, 5.35],
    rotation: [-2.556, 0.248, 2.32],
    scale: 1.5,
  },
  {
    name: "Zeppelin",
    position: [34.303, -4.448, 15.122],
    rotation: [2.144, 1.232, -1.969],
    scale: 1,
  },
];

export default function Model({ scroll, ...props }) {
  const group = useRef();
  const meshRefs = useRef({});
  const { nodes, materials, animations } = useGLTF("/model.glb");
  const { actions } = useAnimations(animations, group);

  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (actions["CameraAction.005"]) {
      const action = actions["CameraAction.005"];
      action.play().paused = true;
    } else {
      console.error("CameraAction.005 not found");
    }
  }, [actions]);

  useEffect(() => {
    document.body.style.cursor = hovered ? "pointer" : "auto";
    return () => {
      document.body.style.cursor = "auto";
    };
  }, [hovered]);

  useFrame(() => {
    if (actions["CameraAction.005"]) {
      const action = actions["CameraAction.005"];
      const targetTime = action.getClip().duration * (scroll?.current || 0);
      action.time = THREE.MathUtils.lerp(action.time, targetTime, 0.05);
    }

    for (const name of Object.keys(meshRefs.current)) {
      const mesh = meshRefs.current[name];
      if (mesh?.material?.color) {
        mesh.material.color.lerp(
          color.set(hovered === name ? "#21efef" : "white"),
          hovered ? 0.1 : 0.05
        );
      }
    }
  });

  return (
    <group ref={group} {...props} dispose={null}>
      <Environment preset="night" resolution={10} intensity={0.4} />

      <group
        name="OverallParent"
        position={[0.061, 4.042, 0.346]}
        scale={0.25}
        onPointerOver={(e) => (e.stopPropagation(), setHovered(e.object.name))}
        onPointerOut={(e) => (e.stopPropagation(), setHovered(null))}
      >
        {MESH_ITEMS.map((item) => (
          <mesh
            key={item.name}
            ref={(el) => { if (el) meshRefs.current[item.name] = el; }}
            name={item.name}
            geometry={nodes[item.name].geometry}
            position={item.position}
            rotation={item.rotation}
            scale={item.scale}
          >
            <meshPhysicalMaterial
              metalness={0.05}
              roughness={0.02}
              transmission={0.85}
              thickness={3}
              ior={2.42}
              envMapIntensity={2}
              clearcoat={1}
              clearcoatRoughness={0}
              reflectivity={1}
              specularIntensity={1.5}
              specularColor="#ffffff"
              attenuationColor="#e8eeff"
              attenuationDistance={5}
              toneMapped={false}
            />
          </mesh>
        ))}
      </group>
      <group name="Camera" position={[-10.722, -5.981, 2.297]} rotation={[1.363, 0.561, 1.948]}>
        <PerspectiveCamera
          makeDefault={true}
          far={100}
          near={0.1}
          fov={22.895}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <directionalLight
            position={[0, 0, 0]}
            intensity={2}
          />
        </PerspectiveCamera>
      </group>
    </group>
  );
}

useGLTF.preload("/model.glb");
